/* Full collection-backed free-pattern shelf; Liquid remains the safe fallback. */
(() => {
  'use strict';
  if (window.__miniamiFreePatternLoop) return;
  window.__miniamiFreePatternLoop = true;

  const selector = '[data-miniami-free-pattern][data-miniami-loop-source]';
  const instances = new WeakMap();
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const roots = (node) => [
    ...(node.matches?.(selector) ? [node] : []),
    ...(node.querySelectorAll?.(selector) || []),
  ];

  function collectionURL(path, page) {
    const url = new URL(path, location.origin);
    if (url.origin !== location.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?collections\/[^/]+\/?$/i.test(url.pathname)) {
      throw new Error('Not a same-origin collection');
    }
    url.pathname = `${url.pathname.replace(/\/$/, '')}/products.json`;
    url.search = new URLSearchParams({ limit: '100', page: String(page) }).toString();
    return url;
  }

  async function loadAll(path, signal) {
    const products = [];
    const seen = new Set();
    for (let page = 1; page <= 6; page += 1) {
      const response = await fetch(collectionURL(path, page), {
        credentials: 'same-origin', headers: { Accept: 'application/json' }, signal,
      });
      if (!response.ok) throw new Error(`Collection page ${page}: HTTP ${response.status}`);
      const data = await response.json();
      if (!Array.isArray(data.products) || data.products.length > 100) throw new Error('Invalid collection page');
      if (page === 6) {
        if (data.products.length) throw new Error('Collection exceeds 500 products');
        break;
      }
      for (const product of data.products) {
        if (!product?.id || !product.handle || !product.title) throw new Error('Invalid product');
        if (seen.has(String(product.id))) continue;
        seen.add(String(product.id));
        products.push(product);
      }
      if (data.products.length < 100) break;
    }
    return products;
  }

  function makeCard(product, path) {
    const link = document.createElement('a');
    const prefix = new URL(path, location.origin).pathname.split('/collections/')[0];
    link.href = `${prefix}/products/${encodeURIComponent(product.handle)}`;
    link.className = 'miniami-parallel-free__product-card';
    link.setAttribute('data-free-pattern-product-card', '');
    link.setAttribute('data-image-frame', '');
    link.setAttribute('data-optional-media', '');
    const media = document.createElement('span');
    media.className = 'miniami-parallel-free__product-media';
    const source = product.images?.[0];
    if (source?.src) {
      try {
        const src = new URL(source.src, location.origin);
        if (src.protocol === 'https:' && /(?:^|\.)shopify\.com$/.test(src.hostname)) {
          src.searchParams.set('width', '340');
          const image = document.createElement('img');
          image.src = src.href;
          image.alt = source.alt || window.MiniamiTitleBase(product.title);
          image.width = 170;
          image.height = 170;
          image.loading = 'lazy';
          image.decoding = 'async';
          media.append(image);
        }
      } catch (_) { /* Keep the empty media bed. */ }
    }
    const meta = document.createElement('span');
    meta.className = 'miniami-parallel-free__product-meta';
    const title = document.createElement('span');
    title.textContent = window.MiniamiTitleBase(product.title);
    const price = document.createElement('span');
    const currency = window.Shopify?.currency?.active;
    const amounts = (product.variants || []).map((variant) => Number(variant.price)).filter(Number.isFinite);
    if (/^[A-Z]{3}$/.test(currency || '') && amounts.length) {
      try {
        price.textContent = new Intl.NumberFormat(document.documentElement.lang || navigator.language, {
          style: 'currency', currency,
        }).format(Math.min(...amounts));
      } catch (_) { /* Never invent a currency. */ }
    }
    meta.append(title, price);
    link.append(media, meta);
    return link;
  }

  function init(root) {
    if (instances.has(root)) return;
    const shelf = root.querySelector('[data-free-pattern-shelf]');
    const viewport = shelf?.querySelector('[data-miniami-loop-viewport]');
    const track = viewport?.querySelector('[data-miniami-loop-track]');
    const primary = track?.querySelector('[data-miniami-loop-primary]');
    const clone = track?.querySelector('[data-miniami-loop-clone]');
    if (!primary || !clone) return;
    const controller = new AbortController();
    const { signal } = controller;
    let resizeObserver;
    let visible = true;
    const staticMode = () => Boolean(reduce?.matches || connection?.saveData || document.hidden
      || document.documentElement.dataset.miniamiMotionForcedStatic === 'true'
      || window.MiniamiMotion?.isForcedStatic?.());
    const sync = () => {
      root.dataset.miniamiLoopStatic = String(staticMode());
      track.style.animationPlayState = !visible || document.hidden ? 'paused' : '';
    };
    const measure = () => {
      if (signal.aborted || root.dataset.miniamiLoopInventory !== 'complete') return;
      root.dataset.miniamiLoopMeasure = 'true';
      const distance = clone.offsetLeft - primary.offsetLeft;
      const equal = Math.abs(primary.getBoundingClientRect().width - clone.getBoundingClientRect().width) < 0.6;
      delete root.dataset.miniamiLoopMeasure;
      const ready = equal && distance > viewport.clientWidth && primary.children.length > 1;
      track.style.setProperty('--miniami-loop-distance', `${-distance}px`);
      track.style.setProperty('--miniami-loop-duration', `${Math.max(12, distance / 36)}s`);
      root.dataset.miniamiLoopReady = String(ready);
    };
    const on = (target, event, handler, options = {}) => target?.addEventListener?.(event, handler, { ...options, signal });
    on(document, 'visibilitychange', sync);
    on(document, 'miniami:motion-change', sync);
    on(reduce, 'change', sync);
    on(connection, 'change', sync);
    on(window, 'resize', measure, { passive: true });
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        visible = Boolean(entries[0]?.isIntersecting);
        sync();
      }, { rootMargin: '120px 0px' });
      observer.observe(viewport);
      signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    }
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(viewport);
      resizeObserver.observe(primary);
      signal.addEventListener('abort', () => resizeObserver.disconnect(), { once: true });
    }
    instances.set(root, { dispose: () => { controller.abort(); instances.delete(root); } });
    sync();
    // Liquid already rendered the current collection with the product-role
    // filter, native presentment prices and the matching inert clone. The
    // public products.json endpoint has no role metafields; replacing these
    // cards from it would reintroduce add-ons/bundles and lose their IDs.
    const serverCards = [...primary.children];
    if (serverCards.length > 0 && serverCards.every((card) =>
      card.matches('a[data-free-pattern-product-card][data-shopify-product-id][data-free-pattern-product-role="standalone_pattern"]'))
      && clone.children.length === serverCards.length) {
      root.dataset.miniamiLoopInventory = 'complete';
      root.dataset.miniamiLoopCount = String(serverCards.length);
      measure();
      serverCards.forEach((card) => on(card.querySelector('img'), 'load', measure, { once: true }));
      return;
    }
    loadAll(root.dataset.miniamiLoopSource, signal).then((products) => {
      if (signal.aborted || !root.isConnected) return;
      if (!products.length) { root.dataset.miniamiLoopInventory = 'empty'; return; }
      const cards = products.map((product) => makeCard(product, root.dataset.miniamiLoopSource));
      // Never replace the SSR sample with a partial page after a fetch failure.
      root.dataset.miniamiLoopReady = 'false';
      primary.replaceChildren(...cards);
      clone.replaceChildren(...cards.map((card) => {
        const copy = document.createElement('span');
        copy.className = card.className;
        copy.innerHTML = card.innerHTML;
        copy.querySelectorAll('img').forEach((image) => { image.alt = ''; });
        return copy;
      }));
      root.dataset.miniamiLoopInventory = 'complete';
      root.dataset.miniamiLoopCount = String(cards.length);
      measure();
      primary.querySelectorAll('img').forEach((image) => {
        on(image, 'load', measure, { once: true });
        on(image, 'error', () => { image.remove(); measure(); }, { once: true });
      });
    }).catch((error) => {
      if (signal.aborted) return;
      root.dataset.miniamiLoopInventory = 'fallback';
      root.dataset.miniamiLoopReady = 'false';
      console.warn('[Miniami free-pattern shelf] Retaining Liquid cards:', error);
    });
  }

  const boot = (node = document) => roots(node).forEach(init);
  document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => roots(event.target).forEach((root) => instances.get(root)?.dispose()));
  if (document.readyState !== 'loading') boot();
})();
